import { ChangeDetectionStrategy, Component, Input, signal } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatFacades from '@medsurf/flat-facades';
import { ZodSharedValidator } from '@medsurf/flat-validators';
import { ModalControlComponent } from "@medsurf/flat-components";
import { DialogService } from "primeng/dynamicdialog";
import { TranslateService } from "@ngx-translate/core";

@Component({
    selector: 'medsurf-common-media-library-media-sidebar-copyright',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './copyright.component.html',
    styleUrls: ['./copyright.component.scss'],
    standalone: false
})
export class CopyrightComponent {
  @Input({required: true}) set mediaCopyright(value: FlatModels.MediaEntityModels.MediaCopyright) {
    this.#mediaCopyright = structuredClone(value);
    if (this.#mediaCopyright) {
      this.copyrightForm.reset({
        id: this.#mediaCopyright.copyright,
        text: this.#mediaCopyright.text
      });
    } else {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        'notification.no_entity_found',
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "APPS.Author.CopyrightComponent.set_mediaCopyright"
      );
    }
    this.#isSubmitted.set(false);
  }
  get mediaCopyright() {
    return this.#mediaCopyright;
  }
  
  #isSubmitted = signal(false);
  #mediaCopyright: FlatModels.MediaEntityModels.MediaCopyright;
  copyrightForm = new FormGroup(
    {
      id: new FormControl(''),
      text: new FormControl(''),
    },
    {
      validators: ZodSharedValidator.validate(
        FlatModels.CopyrightEntityModels.CopyrightSchema.pick({
          id: true,
          text: true
        })
      ),
      updateOn: 'change'
    }
  );
  readonly FlatModels = FlatModels;

  constructor(public alertControlFacade: FlatFacades.AlertControlFacade,
              public mediaAuthorFacade: FlatFacades.MediaAuthorFacade,
              public constantAuthorFacade: FlatFacades.ConstantAuthorFacade,
              public searchControlFacade: FlatFacades.SearchControlFacade,
              public dialogService: DialogService,
              public translate: TranslateService) {
  }

  onSubmit() {
    const media = structuredClone(this.searchControlFacade.snapshot_currentSelectedMedia());
    if (this.#isSubmitted() || this.copyrightForm.invalid || !media) {
      return;
    }
    this.#isSubmitted.set(true);
    
    // Update Media
    if (!media.mediaCopyrights) return
    const index = media.mediaCopyrights.findIndex(mc => mc.id === this.mediaCopyright.id);
    if (index < 0) return;
    media.mediaCopyrights[index].copyright = this.copyrightForm.value.id || '';
    media.mediaCopyrights[index].text = this.copyrightForm.value.text || '';
    this.mediaAuthorFacade.requestUpdateMedia(media).subscribe({
      error: (result) => {
        ZodSharedValidator.handleError(result, this.alertControlFacade, this.copyrightForm);
        this.#isSubmitted.set(false);
      },
      next: () => {
        this.#isSubmitted.set(false);
        this.alertControlFacade.requestAddAlert({
          key: 'main',
          detail: 'notification.saved',
          severity: FlatModels.AlertControlModels.AlertType.enum.success,
          life: FlatModels.AlertControlModels.AlertTime.DEFAULT
        });
      }
    });
  }
  
  removeValue(): void {
    const dialogRef = this.dialogService.open(ModalControlComponent, {
      header: this.translate.instant('global.copyright_remove'),
      data: {
        label: 'global.copyright_remove_confirm',
        inputLabel: 'global.remove',
        inputClass: FlatModels.ButtonDesignModels.ButtonDesignClass.DANGER
      },
      dismissableMask: true,
      width: '50%'
    });
    dialogRef.onClose.subscribe(remove => {
      if (remove) {
        const media = structuredClone(this.searchControlFacade.snapshot_currentSelectedMedia());
        if (media && media.mediaCopyrights) {
          media.mediaCopyrights = media.mediaCopyrights.filter(mc => mc.id !== this.mediaCopyright.id);
          this.mediaAuthorFacade.requestUpdateMedia(media).subscribe({
            error: (result) => {
              ZodSharedValidator.handleError(result, this.alertControlFacade, this.copyrightForm);
              this.#isSubmitted.set(false);
            },
            next: () => {
              this.#isSubmitted.set(false);
              this.alertControlFacade.requestAddAlert({
                key: 'main',
                detail: 'notification.saved',
                severity: FlatModels.AlertControlModels.AlertType.enum.success,
                life: FlatModels.AlertControlModels.AlertTime.DEFAULT
              });
            }
          });
        }
      }
    });
  }
}
